import { RotateCcw, Home, LifeBuoy } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { isRouteErrorResponse, Link, useRouteError } from 'react-router';
import { Button } from '@/components/ui';
import { BRAND } from '@/brand';

const Recovery = ({ title, detail, onRetry }: { readonly title: string; readonly detail?: string; readonly onRetry?: () => void }) => (
  <div role="alert" className="mx-auto flex max-w-lg flex-col items-center gap-3 px-6 py-16 text-center">
    <span className="flex size-14 items-center justify-center rounded-full bg-warning-soft text-warning" aria-hidden>
      <LifeBuoy className="size-6" />
    </span>
    <h1 className="text-xl font-semibold text-text">{title}</h1>
    <p className="text-sm text-text-muted">
      Your data is safe — nothing was lost. Try again, or head home and come back to this in a moment.
    </p>
    <div className="mt-2 flex flex-wrap justify-center gap-2">
      {onRetry ? (
        <Button onClick={onRetry} icon={<RotateCcw aria-hidden />}>
          Try again
        </Button>
      ) : null}
      <Button asChild variant="outline">
        <Link to="/">
          <Home aria-hidden />
          Go to Home
        </Link>
      </Button>
    </div>
    {detail ? (
      <details className="mt-4 w-full text-left text-xs text-text-muted">
        <summary className="cursor-pointer">Technical details</summary>
        <pre className="mt-2 overflow-auto whitespace-pre-wrap rounded-md bg-surface-sunken p-3">{detail}</pre>
      </details>
    ) : null}
  </div>
);

interface State { readonly error: Error | null }

/** Catches render errors in a subtree and offers a friendly recovery. `resetKey` change clears the error. */
export class ErrorBoundary extends Component<{ readonly children: ReactNode; readonly resetKey?: string }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Mintage UI error', error, info.componentStack);
  }

  override componentDidUpdate(prev: { readonly resetKey?: string }): void {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  override render(): ReactNode {
    if (this.state.error) {
      return <Recovery title="This screen hit a snag" detail={this.state.error.message} onRetry={() => this.setState({ error: null })} />;
    }
    return this.props.children;
  }
}

/** Router `errorElement`: loader/lazy-import failures and thrown responses. */
export const RouteError = () => {
  const err = useRouteError();
  if (isRouteErrorResponse(err) && err.status === 404) return <Recovery title="We couldn't find that page" />;
  const detail = err instanceof Error ? err.message : isRouteErrorResponse(err) ? `${err.status} ${err.statusText}` : String(err);
  const chunk = /Failed to fetch dynamically imported module|Importing a module script failed/.test(detail);
  return (
    <Recovery
      title={chunk ? `${BRAND.name} was updated` : 'This screen hit a snag'}
      detail={detail}
      onRetry={() => window.location.reload()}
    />
  );
};
